import { AbsenceStatus, dateAdd, DASHBOARD_TILES, GetCommonWorkDaysParams, toDateString } from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import "./dashboard-tile";
import "../employee-absence-list";
import { DateString } from "@pentacode/openapi";
import { app } from "../../init";
import { Task } from "@lit/task";
import { shared } from "../../styles";
import type { EmployeeAbsenceListItem } from "../employee-absence-list";

@customElement("ptc-dashboard-tile-sick-leave")
export class DashboardTileSickLeave extends LitElement {
    @state()
    from: DateString = new Date().toISOString() as DateString;

    @state()
    to: DateString = dateAdd(new Date().toISOString() as DateString, {
        years: 1,
    }) as DateString;

    private _absenceTask = new Task(this, {
        task: async ([api]) => {
            const absences = await api
                .getAbsences({
                    from: this.from,
                    to: this.to,
                    status: [AbsenceStatus.Requested],
                })
                .then((absences) =>
                    absences.filter(
                        (absence) => absence.status === AbsenceStatus.Requested && absence.type.includes("sick")
                    )
                );

            const groupedAbsences = Object.groupBy(absences, (absence) => absence.employeeId);

            const employeeAbsenceList: EmployeeAbsenceListItem[] = (
                await Promise.all(
                    Object.entries(groupedAbsences).map(async ([employeeId, absences = []]) => {
                        const employee = app.getEmployee(Number(employeeId));
                        if (!employee) {
                            return null;
                        }
                        let commonWorkDays: number[] = [];

                        const contract = employee.getContractForDate(new Date().toISOString() as DateString);
                        if (contract && contract.fixedWorkDays && contract.hoursPerDay) {
                            commonWorkDays = contract.hoursPerDay
                                .map((hours, index) => {
                                    if (hours > 0) {
                                        // sunday would correspond to position 6 in the fixedWorkDays
                                        if (index === 6) return 0;
                                        return index + 1;
                                    }
                                    return -1;
                                })
                                .filter((index) => index !== -1);
                        } else {
                            commonWorkDays = await app.api.getCommonWorkDays(
                                new GetCommonWorkDaysParams({
                                    employeeId: Number(employeeId),
                                    before: toDateString(new Date()),
                                })
                            );
                        }

                        return {
                            employee,
                            absences,
                            commonWorkDays,
                        };
                    })
                )
            ).filter((entry): entry is EmployeeAbsenceListItem => entry !== null && entry.employee !== null);

            return employeeAbsenceList;
        },

        args: () => [app.api, this.from, this.to] as const, // @TODO: is app.api needed?
        autoRun: true,
    });

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }
        `,
    ];

    render() {
        return html`
            <ptc-dashboard-tile
                style="--tile-icon-color: var(--orange)"
                label=${DASHBOARD_TILES.sickLeaveRequests}
                icon="face-thermometer"
                .size=${{ inline: 1, block: 2 }}
            >
                ${this._absenceTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-spinner
                            class="place-self-center double-padded"
                            active
                            style="font-size: 1rem"
                        ></ptc-spinner>
                    `,
                    error: () =>
                        html`<div class="double-padded text-centering">
                            Krankmeldungen konnten nicht geladen werden!
                        </div>`,
                    complete: (absences) => {
                        return html`<ptc-employee-absence-list
                            style="--absence-duration-color: var(--orange)"
                            .employeeAbsences=${absences}
                        >
                            <div slot="empty-message" class="double-padded text-centering subtle place-self-center">
                                <span class="emoji huger">💪</span>
                                <p>Alle Krankmeldungen bearbeitet</p>
                            </div>
                        </ptc-employee-absence-list>`;
                    },
                })}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-sick-leave": DashboardTileSickLeave;
    }
}
